import React, {useCallback} from 'react';
import {TableRow, Checkbox, Tooltip, IconButton, Typography} from '@material-ui/core';
import {Info, ExpandLess, ExpandMore} from '@material-ui/icons';
import millify from 'millify';
import {getPercents, getDisplayName, isNetResellerRevenueSource} from '../helpers/dataHelpers';
import Insights from './Insights';
import AnalyticsInsights from './AnalyticsInsights';
import {StyledTableCell} from './StyledTableCell';
import useRowObscured from '../hooks/useRowObscured';
import {nf} from '../helpers/dataHelpers';
import CarouselNavigationCell from './CarouselNavigationCell';
import Trend from '../../Trend';
import {Skeleton} from '@material-ui/lab';

const formatStackedValue = value => {
  if (typeof value !== 'number' || Number.isNaN(value)) return value;
  return value.toLocaleString('en-US', {maximumFractionDigits: 2});
};

const DirectIndicator = ({resellerValue, theme}) => {
  if (!resellerValue) return null;

  return (
    <Tooltip title={`Direct on ${resellerValue.join(', ')}.`} arrow>
      <div
        style={{
          display: 'inline-block',
          alignItems: 'center',
          padding: 4,
          borderRadius: 4,
          marginLeft: 8,
          position: 'relative',
          maxHeight: 20,
          lineHeight: '20px',
          top: -2,
        }}
      >
        <Typography
          variant="caption"
          display="inline-block"
          style={{
            color: theme.palette.primary.contrastText,
            background: theme.palette.graphColors.eCPM,
            padding: '4px 8px',
            borderRadius: 4,
            fontWeight: 'bold',
            fontSize: 10,
          }}
        >
          DIRECT
        </Typography>
        {resellerValue.length > 1 && (
          <Typography
            variant="caption"
            display="inline-block"
            color="textSecondary"
            style={{
              padding: 4,
              background: theme.palette.background.default,
              position: 'relative',
              borderTopRightRadius: 4,
              borderBottomRightRadius: 4,
            }}
          >
            {resellerValue.length}
          </Typography>
        )}
      </div>
    </Tooltip>
  );
};

const PlainDirectIndicator = ({enabled, theme}) => {
  if (!enabled) return null;

  return (
    <div
      style={{
        display: 'inline-block',
        alignItems: 'center',
        padding: 4,
        borderRadius: 4,
        marginLeft: 8,
        position: 'relative',
        maxHeight: 20,
        lineHeight: '20px',
        top: -2,
      }}
    >
      <Typography
        variant="caption"
        display="inline-block"
        style={{
          color: theme.palette.primary.contrastText,
          background: theme.palette.graphColors.eCPM,
          padding: '4px 8px',
          borderRadius: 4,
          fontWeight: 'bold',
          fontSize: 10,
        }}
      >
        DIRECT
      </Typography>
    </div>
  );
};

const NetIndicator = ({enabled, theme, commissionPercent}) => {
  if (!enabled) return null;

  const tooltip =
    commissionPercent != null && commissionPercent !== ''
      ? `Revenue shown after your ${commissionPercent}% commission is deducted.`
      : 'Revenue shown after commission is deducted.';

  return (
    <Tooltip title={tooltip} arrow>
      <div
        style={{
          display: 'inline-block',
          alignItems: 'center',
          padding: 4,
          borderRadius: 4,
          marginLeft: 8,
          position: 'relative',
          maxHeight: 20,
          lineHeight: '20px',
          top: -2,
        }}
      >
        <Typography
          variant="caption"
          display="inline-block"
          style={{
            color: theme.palette.secondary.contrastText,
            background: theme.palette.secondary.main,
            padding: '4px 8px',
            borderRadius: 4,
            fontWeight: 'bold',
            fontSize: 10,
          }}
        >
          NET REVENUE
        </Typography>
      </div>
    </Tooltip>
  );
};

const SourceRow = React.memo(
  ({
    visibleMetrics,
    isMetricVisible,
    showNavigationCells,
    insights,
    shouldGraphStick,
    stickyGraphRegionHeightPx,
    source,
    sourcesConfig,
    metricsConfig,
    demandData,
    comparisonDemandData,
    comparisonEnabled,
    comparisonLoading,
    disabledSources,
    enabledMetric,
    toggleSource,
    toggleMetric,
    setHover,
    cancelHover,
    classes,
    theme,
    disableMillify,
    resellerValue,
    scrollContainerRef,
    registerRowRef,
    isSearchMatch = false,
    // Expansion props
    isPrebid = false,
    isExpanded = false,
    onToggleExpansion,
    // Report type for conditional Insights rendering
    reportType,
    netRevenueLabelContext = null,
  }) => {
    const showNetIndicator =
      netRevenueLabelContext &&
      isNetResellerRevenueSource(
        source,
        netRevenueLabelContext.stackType,
        netRevenueLabelContext.directSourcesSet
      );
    const getSourceLabel = src => getDisplayName(src, sourcesConfig);
    const [rowRef, isObscured] = useRowObscured(
      shouldGraphStick,
      scrollContainerRef,
      stickyGraphRegionHeightPx
    );
    const isSourceActive = !disabledSources.includes(source);

    const mergedRowRef = useCallback(
      el => {
        if (typeof rowRef === 'function') rowRef(el);
        else if (rowRef && typeof rowRef === 'object') rowRef.current = el;
        if (registerRowRef) registerRowRef(source, el);
      },
      [rowRef, registerRowRef, source]
    );

    // Disable expansion for prebid rows for now
    const expandPrebidRowEnabled = isPrebid && false;

    const handleToggleSource = useCallback(
      e => {
        if (e) e.stopPropagation();
        toggleSource(source);
      },
      [toggleSource, source]
    );

    const handleMetricClick = useCallback(
      (e, dataField) => {
        if (e) e.stopPropagation();
        toggleMetric(dataField);
      },
      [toggleMetric]
    );

    const metricsFiltered =
      (visibleMetrics || Object.keys(metricsConfig.perSource))?.filter(
        metric => !isMetricVisible || isMetricVisible(metric)
      ) || [];

    return (
      <TableRow
        ref={mergedRowRef}
        hover
        alignItems="center"
        spacing={2}
        container
        className={classes.sourceRow}
        onClick={handleToggleSource}
        onPointerEnter={() => setHover(source)}
        onPointerLeave={cancelHover}
        style={{
          opacity: !(isObscured || !isSourceActive) ? 1 : 0.3,
          background: insights?.[source]?.['inactive'] ? 'rgba(231, 76, 60,.1)' : 'inherit',
        }}
      >
        <StyledTableCell
          style={{
            borderRight: `1px dashed ${theme.palette.divider}`,
            width: 450,
            verticalAlign: 'middle',
          }}
        >
          <div
            style={{
              position: 'relative',
              minHeight: 36,
              lineHeight: '36px',
              textAlign: 'left',
              textTransform: 'capitalize',
              fontWeight: 'bold',
            }}
          >
            {expandPrebidRowEnabled && (
              <Tooltip title="Show revenue distribution by eCPM" arrow>
                <IconButton
                  onClick={e => {
                    e.stopPropagation();
                    onToggleExpansion();
                  }}
                  size="small"
                  style={{position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)'}}
                >
                  {isExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Tooltip>
            )}
            <Tooltip
              arrow
              title={
                !disabledSources.includes(source)
                  ? `Remove ${getSourceLabel(source)}`
                  : `Add ${getSourceLabel(source)}`
              }
            >
              <Checkbox checked={isSourceActive} color={'primary'} style={{marginRight: 16}} />
            </Tooltip>

            {sourcesConfig[source].logo && (
              <img
                src={sourcesConfig[source].logo}
                alt={source}
                className={classes.domainImage}
                style={{verticalAlign: 'middle', marginRight: 8}}
              />
            )}

            {getSourceLabel(source)}

            <DirectIndicator resellerValue={resellerValue} theme={theme} />
            <PlainDirectIndicator enabled={sourcesConfig[source].direct} theme={theme} />
            <NetIndicator
              enabled={showNetIndicator}
              theme={theme}
              commissionPercent={netRevenueLabelContext?.resellerCommission}
            />
            {insights?.[source] &&
              (reportType === 'analytics' ? (
                <AnalyticsInsights
                  isSourceActive={isSourceActive}
                  insights={insights}
                  source={source}
                  onMouseEnter={() => setHover(source, insights[source])}
                  onMouseLeave={() => setHover(source)}
                />
              ) : (
                <Insights
                  isSourceActive={isSourceActive}
                  insights={insights[source]}
                  onMouseEnter={() => setHover(source, insights[source])}
                  onMouseLeave={() => setHover(source)}
                />
              ))}

            {sourcesConfig[source].tooltip && (
              <Tooltip title={sourcesConfig[source].tooltip}>
                <IconButton
                  size="small"
                  style={{verticalAlign: 'middle', marginLeft: 8, marginTop: -5}}
                >
                  <Info />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </StyledTableCell>
        {showNavigationCells && (
          <CarouselNavigationCell
            isEmpty
            isSearchMatch={isSearchMatch}
            isRightEdge={false}
            theme={theme}
          />
        )}
        {metricsFiltered.map(metric => {
          const config = metricsConfig.perSource[metric];
          // Day-level metrics (e.g. Ad Requests) are the same for every source and must
          // not be summed across sources — reuse the period total on each row.
          const totalBySourceAndMetric = config.dayLevelOnly
            ? nf(demandData.totals?.[config.dataField] ?? 0)
            : nf(demandData.totals?.perSource[`${source} ${config.dataField}`] ?? 0);

          const previousRaw = config.dayLevelOnly
            ? comparisonDemandData?.totals?.[config.dataField]
            : comparisonDemandData?.totals?.perSource?.[`${source} ${config.dataField}`];
          const previousBySourceAndMetric =
            previousRaw !== undefined && previousRaw !== null ? nf(previousRaw) : null;

          const isMetricActive = enabledMetric.includes(config.dataField);
          const isSourceActive = !disabledSources.includes(source);

          const tooltipText = !isMetricActive
            ? `Show ${metric}`
            : `${isSourceActive ? 'Remove' : 'Add'} ${getSourceLabel(source)}`;

          return (
            <Tooltip arrow title={tooltipText} key={metric}>
              <StyledTableCell
                className="metricCell"
                onClick={e =>
                  !isMetricActive ? handleMetricClick(e, config.dataField) : handleToggleSource(e)
                }
                align="left"
                style={{
                  verticalAlign: 'middle',
                  opacity:
                    !isMetricActive || disabledSources.includes(source)
                      ? 0.3
                      : 1,
                  backgroundColor: (() => {
                    const sorted =
                      isSourceActive &&
                      enabledMetric.includes(metricsConfig.perSource[metric].dataField);
                    if (sorted) return theme.palette.graphColors.sorted;
                    return 'inherit';
                  })(),
                }}
              >
                <Typography
                  style={{
                    display: 'block',
                    minWidth: 160,
                    fontWeight: 'bold',
                  }}
                >
                  {formatStackedValue(
                    disableMillify || config.disableMillify
                      ? parseFloat(Number(totalBySourceAndMetric).toFixed(2))
                      : millify(totalBySourceAndMetric)
                  )}
                  {config.symbol}{' '}
                  {config.cumulative &&
                    config.comparisonWithTotalEnabled &&
                    !config.dayLevelOnly && (
                      <Typography variant={'caption'} display="inline" color={'textSecondary'}>
                        (
                        {getPercents(totalBySourceAndMetric, demandData.totals[config.dataField])}
                        %)
                      </Typography>
                    )}
                  {comparisonEnabled &&
                    !comparisonLoading &&
                    previousBySourceAndMetric !== null &&
                    !isNaN(previousBySourceAndMetric) && (
                      <>
                        {' '}
                        <Trend
                          invertTrend={config.invertTrend}
                          inDropdown
                          current={Number(totalBySourceAndMetric)}
                          previous={Number(previousBySourceAndMetric)}
                        />
                      </>
                    )}
                  {comparisonEnabled && comparisonLoading && (
                    <span style={{display: 'inline-block', marginLeft: 4, width: 32}}>
                      <Skeleton variant="rect" height={10} style={{borderRadius: 4}} />
                    </span>
                  )}
                </Typography>

                {shouldGraphStick &&
                  enabledMetric.includes(metricsConfig.perSource[metric].dataField) &&
                  !disabledSources.includes(source) &&
                  !config.dayLevelOnly &&
                  !metricsConfig.perSource[metric].hideIndicator && (
                    <>
                      {config.indicatorType === 'column' && (
                        <div
                          style={{
                            ...config.indicatorStyle,
                            background: `linear-gradient(0deg, ${sourcesConfig[source].color} 0%, ${
                              sourcesConfig[source].color
                            } ${getPercents(
                              totalBySourceAndMetric,
                              demandData.totals[config.dataField]
                            )}%, rgba(0,0,0,0) ${
                              parseInt(
                                getPercents(
                                  totalBySourceAndMetric,
                                  demandData.totals[config.dataField]
                                )
                              ) + 0
                            }%)`,
                          }}
                        />
                      )}
                      {config.indicatorType === 'line' && (
                        <div
                          style={{
                            ...config.indicatorStyle,
                            background: sourcesConfig[source].color,
                          }}
                        />
                      )}
                      {config.indicatorType === 'dashed-line' && (
                        <div
                          style={{
                            ...config.indicatorStyle,
                            borderBottom: `4px dashed ${sourcesConfig[source].color}`,
                          }}
                        />
                      )}
                    </>
                  )}
              </StyledTableCell>
            </Tooltip>
          );
        })}
        {showNavigationCells && (
          <CarouselNavigationCell isEmpty isSearchMatch={isSearchMatch} isRightEdge theme={theme} />
        )}
      </TableRow>
    );
  }
);

export default SourceRow;
